import {
  buildThemeSeedFromPaletteV3,
  READING_THEME_ENGINE_VERSION,
  resolveReadingTheme,
  resolveReadingThemeMode,
  type ReadingThemeMode,
  type ReadingThemeModePref,
} from "../../../config/readingTheme";

export type DerivedReadingTheme =
  | { status: "ok"; tokens: Record<string, string>; mode: ReadingThemeMode }
  /** A capa foi lida e o gate reprovou mesmo após o recuo. */
  | { status: "declined" };

/**
 * Paleta da capa → tema v3 (`docs/systems/reading-theme-auto.md` §1). O ÚNICO
 * caminho de derivação do client: o card de Ajustes (o que o PRO grava) e a
 * prévia da `/pro`/Home (o que se promete a quem não é PRO) passam por aqui,
 * então a prévia não tem como vender um tema diferente do entregue.
 *
 * `pref` ausente = `auto`: quem nunca mexeu no seletor ganha o modo que a capa
 * pede (média de luminância).
 */
export function deriveReadingTheme(
  palette: readonly string[],
  meanLuminance: number | null | undefined,
  pref: ReadingThemeModePref | null | undefined,
): DerivedReadingTheme {
  const mode = resolveReadingThemeMode(pref, meanLuminance);
  const seed = buildThemeSeedFromPaletteV3(palette, mode);
  if (!seed) return { status: "declined" };
  const result = resolveReadingTheme(seed);
  if (result.status !== "ok" || !result.tokens) return { status: "declined" };
  return { status: "ok", tokens: result.tokens, mode };
}

export interface SavedReadingThemeKey {
  userBookId: string | null | undefined;
  /** Ausente em tema gravado antes da v3 (27/09). */
  coverUrl?: string | null;
}

/**
 * O tema salvo ainda é o da leitura atual? A chave é LIVRO + CAPA (BUG-297):
 * trocar a capa do mesmo livro (outra edição, capa própria) também desatualiza.
 *
 * Tema gravado sem `coverUrl` (v2, ou capa `http://` que o schema não aceita
 * como chave) compara só o livro — marcá-lo desatualizado para sempre por
 * falta de chave empurraria a pessoa a regenerar sem nada ter mudado. Avisar
 * quem tem tema v2 é a fatia 3, com copy própria.
 */
export function isSavedReadingThemeCurrent(
  saved: SavedReadingThemeKey | null | undefined,
  book: { id: string; coverUrl: string | null | undefined },
): boolean {
  if (!saved || saved.userBookId !== book.id) return false;
  if (typeof saved.coverUrl !== "string" || saved.coverUrl.length === 0) return true;
  return saved.coverUrl === (book.coverUrl ?? "");
}

/** Mesmos tokens, mesmos valores — a prévia não traria nada novo. */
export function sameReadingThemeTokens(
  a: Record<string, string> | null | undefined,
  b: Record<string, string> | null | undefined,
): boolean {
  if (!a || !b) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => a[k] === b[k]);
}

/** Chave da capa aceita pelo schema da CF (`https://`, até 2048). */
export function readingThemeCoverKey(coverUrl: string | null | undefined): string | undefined {
  const url = typeof coverUrl === "string" ? coverUrl.trim() : "";
  return url.startsWith("https://") && url.length <= 2048 ? url : undefined;
}

/**
 * Tema salvo por um motor anterior ao atual? `engineVersion` ausente = v2
 * (todo tema gravado antes de 27/09). Só decide se o card OFERECE atualizar —
 * nunca dispara geração sozinho (`reading-theme-auto.md` §6).
 */
export function isReadingThemeOutdated(
  saved: { tokens?: Record<string, string> | null; engineVersion?: number } | null | undefined,
): boolean {
  if (!saved?.tokens) return false;
  return (saved.engineVersion ?? 2) < READING_THEME_ENGINE_VERSION;
}
